import React from 'react';
import { useSelector } from 'react-redux';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import { selectSessionAdvisor } from '@/model/selectors';
import { AdvisorUsageTable } from '@/components/usage-analytics/AdvisorUsageTable';
import { USAGE_METRICS_CONFIG } from '@shared/config/usageMetricsConfig';
import { useAdvisorUsageAnalytics } from '@/hooks/useAdvisorUsageAnalytics';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';

function LoadingSkeleton() {
  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <Skeleton className="mb-8 h-10 w-64 rounded" />

      <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div
            key={i}
            className="rounded-lg border border-gray-200 bg-white p-4"
          >
            <Skeleton className="mb-2 h-4 w-24 rounded" />
            <Skeleton className="h-8 w-16 rounded" />
          </div>
        ))}
      </div>

      <Skeleton className="mb-6 h-12 w-full rounded" />
      <Skeleton className="h-96 w-full rounded" />
    </main>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader title="Usage Dashboard" />

      <Alert
        variant="destructive"
        className="mt-4"
      >
        <AlertDescription>
          <div className="space-y-4">
            <p className="font-semibold">Error Loading Usage Data</p>
            <p>{error}</p>
          </div>
        </AlertDescription>
      </Alert>
    </main>
  );
}

export function AdvisorUsageAnalytics() {
  const advisor = useSelector(selectSessionAdvisor);
  const { usageAnalytics, calculatedMetrics, advisorsList, activeAdvisorsCount } =
    useAdvisorUsageAnalytics(advisor?.advisorId);

  if (usageAnalytics.isLoading) {
    return <LoadingSkeleton />;
  }

  if (usageAnalytics.errorMessage) {
    return <ErrorState error={usageAnalytics.errorMessage} />;
  }

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader title="Usage Dashboard" />

      {/* Top Metrics Bar */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <TooltipProvider>
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
            <MetricCard
              label="Active Advisors"
              value={activeAdvisorsCount}
              description="Number of advisors who have logged in at least once"
            />
            {USAGE_METRICS_CONFIG.slice(0, 5).map(metricDef => {
              const rawValue = calculatedMetrics[metricDef.key];
              let displayValue: string | number;

              if (metricDef.format === 'currency') {
                if (rawValue >= 1000000) {
                  displayValue = `$${(rawValue / 1000000).toFixed(2)}M`;
                } else if (rawValue >= 1000) {
                  displayValue = `$${(rawValue / 1000).toFixed(1)}K`;
                } else {
                  displayValue = `$${rawValue.toLocaleString()}`;
                }
              } else {
                displayValue = rawValue.toLocaleString();
              }

              return (
                <MetricCard
                  key={metricDef.key}
                  label={metricDef.label}
                  value={displayValue}
                  description={metricDef.description}
                />
              );
            })}
          </div>
        </TooltipProvider>
      </div>

      {/* Advisors Table */}
      {advisorsList.length > 0 && <AdvisorUsageTable advisors={advisorsList} />}

      {advisorsList.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 py-12 text-center">
          <p className="text-gray-500">No advisor data available.</p>
        </div>
      )}
    </main>
  );
}

function MetricCard({
  label,
  value,
  description,
}: {
  label: string;
  value: string | number;
  description?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">
          <p className="mb-1 text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </TooltipTrigger>
      {description && (
        <TooltipContent>
          <p>{description}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
